import { Component } from '@angular/core';
import { FormControl, FormBuilder, FormGroup, Validators, ValidatorFn, AbstractControl, ValidationErrors, FormArray } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { debounceTime, of, map, switchMap, Observable, BehaviorSubject } from 'rxjs';
import { ColDef } from 'ag-grid-community';
import { ContactAutocompleteResult } from 'src/app/services/contacts/contact-autocomplete.service';
import { ToastrService } from 'ngx-toastr';

interface NationalRate {
  ltl_rate: any,
  tl_rate: any,
  intermodal_rate: any,
  isCurrent: boolean,
  effectiveStart: string,
  effectiveEnd: string
}

function formatDateForDashboard(raw: any): string {
  const d = new Date(raw);
  if (isNaN(d.getTime())) return String(raw);

  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

@Component({
  selector: 'app-fuel-surcharge',
  templateUrl: './fuel-surcharge.component.html',
  styleUrls: ['./fuel-surcharge.component.scss']
})
export class FuelSurchargeComponent {
  canada_rate: NationalRate = {
    ltl_rate: 'Not Set',
    tl_rate: 'Not Set',
    intermodal_rate: 'Not Set',
    isCurrent: false,
    effectiveStart: '',
    effectiveEnd: ''
  }
  us_rate: NationalRate = {
    ltl_rate: 'Not Set',
    tl_rate: 'Not Set',
    intermodal_rate: 'Not Set',
    isCurrent: false,
    effectiveStart: '',
    effectiveEnd: ''
  }

  // grid stuff
  currentCarriersRowData: any[] = [];
  currentCustomersRowData: any[] = [];
  historicCarriersRowData: any[] = [];
  historicCustomersRowData: any[] = []
  carrierColDefs: ColDef[] = [
    {field: 'contact_organization', headerName: 'Carrier', flex: 3},
    {field: 'effective_start', headerName: 'Start', flex: 1},
    {field: 'effective_end', headerName: 'End', flex: 1},
    {field: 'cost_ltl_rate', headerName: 'LTL', flex: 1, valueGetter: (params) => params.data.cost_ltl_rate !== null ? (+params.data.cost_ltl_rate * 100).toFixed(2) + '%' : 'Not set'},
    {field: 'cost_tl_rate', headerName: 'TL', flex: 1, valueGetter: (params) => params.data.cost_tl_rate !== null ? (+params.data.cost_tl_rate * 100).toFixed(2) + '%' : 'Not set'},
    {field: 'cost_intermodal_rate', headerName: 'Intermodal', flex: 1, valueGetter: (params) => params.data.cost_intermodal_rate !== null ? (+params.data.cost_intermodal_rate * 100).toFixed(2) + '%' : 'Not set'},
    {field: 'cost_ltl_plus_rate', headerName: 'LTL FSC Disc.', flex: 2, valueGetter: (params) => params.data.cost_ltl_plus_rate !== null ? (+params.data.cost_ltl_plus_rate * 100).toFixed(2) : 'Not set'},
    {field: 'cost_tl_plus_rate', headerName: 'LTL FSC Disc.', flex: 2, valueGetter: (params) => params.data.cost_tl_plus_rate !== null ? (+params.data.cost_tl_plus_rate * 100).toFixed(2) : 'Not set'}    
  ];
  customerColDefs: ColDef[] = [
    {field: 'contact_organization', headerName: 'Carrier', flex: 3},
    {field: 'effective_start', headerName: 'Start', flex: 1},
    {field: 'effective_end', headerName: 'End', flex: 1},
    {field: 'chargeable_ltl_rate', headerName: 'LTL', flex: 1, valueGetter: (params) => params.data.chargeable_ltl_rate !== null ? (+params.data.chargeable_ltl_rate * 100).toFixed(2) + '%' : 'Not set'},
    {field: 'chargeable_tl_rate', headerName: 'TL', flex: 1, valueGetter: (params) => params.data.chargeable_tl_rate !== null ? (+params.data.chargeable_tl_rate * 100).toFixed(2) + '%' : 'Not set'},
    {field: 'chargeable_intermodal_rate', headerName: 'Intermodal', flex: 1, valueGetter: (params) => params.data.chargeable_intermodal_rate !== null ? (+params.data.chargeable_intermodal_rate * 100).toFixed(2) + '%' : 'Not set'},
    {field: 'chargeable_ltl_plus_rate', headerName: 'LTL FSC Disc.', flex: 2, valueGetter: (params) => params.data.chargeable_ltl_plus_rate !== null ? (+params.data.chargeable_ltl_plus_rate * 100).toFixed(2) : 'Not set'},
    {field: 'chargeable_tl_plus_rate', headerName: 'LTL FSC Disc.', flex: 2, valueGetter: (params) => params.data.chargeable_tl_plus_rate !== null ? (+params.data.chargeable_tl_plus_rate * 100).toFixed(2) : 'Not set'}    
  ];

  //accordion  UI
  isHistoricCustomerAccordionOpen: boolean = false;
  isHistoricCarrierAccordionOpen: boolean = false;
  isAddRateOpen: boolean = true;

  // add fsc stuff
  onContactSelected(opt: ContactAutocompleteResult){
    this.fsc_form.get('contact_uuid')?.setValue(opt.value);
    this.fsc_form.get('contact_name')?.setValue(opt.label);
  }

  get contactFormControl(): FormControl {
    return this.fsc_form.get('contact_name') as FormControl;
  }

  // old code below

  is_fca_offset: boolean = false; // angular tracks changes better with local variables than form controls??
  is_contact_specific: boolean = false;

  // trying something new...
  conditionSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  condition$: Observable<boolean> = this.conditionSubject.asObservable();

  RATE_TYPES = ['FSC','CUSTOMER','CARRIER'];
  selected_rate_type = this.RATE_TYPES[0];
  find_rate_form = this.fb.group({
    contact_uuid: ['', Validators.required],
    contact_name: ['', Validators.required]
  })
  found_rate_form = this.fb.group({
    start: [new Date()],
    end: [new Date()],
    ltl_fsc: [0.0],
    tl_fsc: [0,0],
    ltl_fca_offset: [0.0],
    tl_fca_offset: [0.0]
  })
  rate_found: boolean = false;

  fsc_url = `api/rates/fsc.php`;
  fsc_form: FormGroup = this.fb.group({
    contact_type: ['', Validators.required],
    contact_uuid: [''],
    contact_name: [''],
    ltl_fsc: [0.0],
    tl_fsc: [0.0],
    start_date: [new Date(), Validators.required],
    end_date: [new Date(), Validators.required],
    ltl_fca_offset: [0.0],
    tl_fca_offset: [0.0],
    intermodal_offset: [0.0],
    rate_type: [this.RATE_TYPES[0]],
    default_area: [''],
    calculation_method: ['', Validators.required],
    schedule_basis: [''],
    offset_basis: [''], // because sometimes the basis doesn't correspond with the contact's country
    currency: ['', Validators.required],
    schedule_brackets: this.fb.array([]),
    schedule_rate_unit: [''] 
  }, {validators: [ratesRequiredValidator(), contactRequiredValidator(), dateRangeValidator()]});

  filtered_contact_list: any[] = [];
  error_message: string[] = [];

  toggleCondition(value: boolean){
    this.conditionSubject.next(value);
  }

  constructor (private http: HttpClient, private fb: FormBuilder, private toastr: ToastrService){
  }

  ngOnInit(){
    const url = "/api/rates/fsc_2.php?action=get_all";
    this.http.get<any>(url).subscribe(
      res => {
        console.log(res);
        if(res.data.current_defaults.canada){
          if (new Date(res.data.current_defaults.canada.effective_end) >= new Date()) this.canada_rate.isCurrent = true;
          this.canada_rate.effectiveStart = formatDateForDashboard(res.data.current_defaults.canada.effective_start);
          this.canada_rate.effectiveEnd = formatDateForDashboard(res.data.current_defaults.canada.effective_end);
          this.canada_rate.intermodal_rate = (+res.data.current_defaults.canada.default_intermodal_rate * 100).toFixed(2) + '%';
          this.canada_rate.tl_rate = (+res.data.current_defaults.canada.default_tl_rate * 100).toFixed(2) + '%';
          this.canada_rate.ltl_rate = (+res.data.current_defaults.canada.default_ltl_rate * 100).toFixed(2) + '%';
        }
        if(res.data.current_defaults.usa){
          if (new Date(res.data.current_defaults.usa.effective_end) >= new Date()) this.us_rate.isCurrent = true;
          this.us_rate.effectiveStart = formatDateForDashboard(res.data.current_defaults.usa.effective_start);
          this.us_rate.effectiveEnd = formatDateForDashboard(res.data.current_defaults.usa.effective_end);
          this.us_rate.intermodal_rate = (+res.data.current_defaults.usa.default_intermodal_rate * 100).toFixed(2) + '%';
          this.us_rate.tl_rate = (+res.data.current_defaults.usa.default_tl_rate * 100).toFixed(2) + '%';
          this.us_rate.ltl_rate = (+res.data.current_defaults.usa.default_ltl_rate * 100).toFixed(2) + '%';
        }
        if(res.data.current_carriers){
          this.currentCarriersRowData = res.data.current_carriers
        }
        if(res.data.current_customers){
          this.currentCustomersRowData = res.data.current_customers
        }
        if(res.data.historic_carriers) this.historicCarriersRowData = res.data.historic_carriers;
        if(res.data.historic_customers) this.historicCustomersRowData = res.data.historic_customers;
      },
      err => {
        console.error(err);
      }
    )

    this.fsc_form.get('contact_name')?.valueChanges.pipe(
      debounceTime(300),
      switchMap(value => {
        const searchTerm = value ?? '';
        return searchTerm.length >= 3 ? this.searchContacts(searchTerm) : [];
      })
    ).subscribe( options => {
      this.filtered_contact_list = options
    })

    this.find_rate_form.get('contact_name')?.valueChanges.pipe(
      debounceTime(300),
      switchMap(value => {
        const searchTerm = value ?? '';
        return searchTerm.length >= 3 ? this.searchContacts(searchTerm) : [];
      })
    ).subscribe( options => {
      this.filtered_contact_list = options
    })

    // automatically set end date when start date is set
    // this.fsc_form.get('start_date')?.valueChanges.subscribe(selectedValue => {
    //   const end_date = new Date(selectedValue);
    //   end_date.setDate(selectedValue.getDate() + 6);
    //   this.fsc_form.get('end_date')?.setValue(end_date);
    // })

    this.fsc_form.get('is_fca_offset')?.valueChanges.subscribe(value => {
      console.log('updating is_fca_offset to ' + value)
      this.is_fca_offset = value;
    })
    this.fsc_form.get('is_contact_specific')?.valueChanges.subscribe(value => {
      console.log('updating is_contact_specific')
      this.is_contact_specific = value;
    })

    this.addScheduleBracket();
  }

  searchContacts(query: string){
    const url = `/api/autocomplete/autocomplete.php?query=${query}`;
    return this.http.get<any>(url).pipe(map(response => response.data));
  }

  addScheduleBracket(){
    this.scheduleBrackets.push(this.createScheduleBracketGroup());
  }

  removeScheduleBracket(index: number){
    this.scheduleBrackets.removeAt(index);
  }

  createScheduleBracketGroup(): FormGroup {
    return this.fb.group({
      min_index: [''],
      max_index: [''],
      rate: [0.0]
    });
  }

  get scheduleBrackets(): FormArray {
    return this.fsc_form.get('schedule_brackets') as FormArray;
  }

  // onContactSelected(event: any, form: number){
  //   const selectedOption = this.filtered_contact_list.find(option => option.contact_organization === event.option.value);
  //   if(selectedOption && form == 1)
  //     this.fsc_form.get('contact_uuid')?.setValue(selectedOption.contact_uuid);
  //   else if (selectedOption && form == 2)
  //     this.find_rate_form.get('contact_uuid')?.setValue(selectedOption.contact_uuid)
  // }

  createErrorMessage(){
    if(this.fsc_form.hasError('fsc_required')){
      this.error_message.push('FSC rate is missing.')
    }
    if(this.fsc_form.hasError('fca_offset_required')){
      this.error_message.push('FCA offset rate missing.')
    }
    if(this.fsc_form.hasError('contact_required')){
      this.error_message.push('Contact has not been selected.')
    }
    if(this.fsc_form.hasError('startDateAfterEndDate')){
      this.error_message.push('Start date needs to be before end date.')
    }
    this.getControlValidationErrors();
  }

  getControlValidationErrors() {
    Object.keys(this.fsc_form.controls).forEach(key => {
      console.log('going through ', key);
      const controlErrors = this.fsc_form.get(key)?.errors;
      if (controlErrors != null) {
        Object.keys(controlErrors).forEach(keyError => {
          this.error_message.push(`Error in ${key}: ${keyError}`)
          console.log('Key control: ' + key + ', keyError: ' + keyError + ', err value: ', controlErrors[keyError]);
        });
      }
    });
  }

  onSubmitFindCharge(){
    // reset previously found charge details
    this.found_rate_form.reset();
    this.rate_found = false;

    // create url to call api
    let final_url = this.fsc_url;
    if (this.selected_rate_type === 'FSC') final_url += '?type=fsc'
    else if (this.selected_rate_type === 'CUSTOMER') final_url += `?type=customer&contact_uuid=${this.find_rate_form.get('contact_uuid')?.value}`
    else if (this.selected_rate_type === 'CARRIER') final_url += `?type=carrier&contact_uuid=${this.find_rate_form.get('contact_uuid')?.value}`
    
    this.http.get<any[]>(final_url).subscribe(
      (response) => {
        if(response.length == 0){
          alert('Rates not found');
          return;
        }

        // sort results by most recent
        // TO DO: PREFERENCE FOR TODAY OR SELECTED DATE
        const sorted_response = response.sort((a,b) => {
          return new Date(b.effective_start).getTime() - new Date(a.effective_start).getTime()
        })

        // populate form to display rate data
        this.found_rate_form.patchValue({
          start: sorted_response[0].effective_start,
          end: sorted_response[0].effective_end,
          ltl_fsc: sorted_response[0].cost_ltl_rate * 100 || sorted_response[0].default_ltl_rate * 100 || sorted_response[0].chargeable_ltl_rate * 100 ,
          tl_fsc: sorted_response[0].cost_tl_rate * 100 || sorted_response[0].default_tl_rate * 100 || sorted_response[0].chargeable_tl_rate * 100,
          ltl_fca_offset: sorted_response[0].cost_ltl_plus_rate * 100 || sorted_response[0].chargeable_ltl_plus_rate * 100 || 0,
          tl_fca_offset: sorted_response[0].cost_tl_plus_rate * 100 || sorted_response[0].chargeable_tl_plus_rate * 100 || 0
        });
        this.rate_found = true;
      },
      (error) => {
        alert('Rates not found');
        console.error(error)
      }
    )
  }

  onSubmit(){
    this.error_message = [];
    this.fsc_form.markAllAsTouched();
    if(this.fsc_form.invalid){
      this.createErrorMessage();
      console.log(this.fsc_form.errors)
      return;
    }

    type PostData = {[key: string]: any};
    let postData: PostData = {
      effective_start: this.fsc_form.get('start_date')?.value,
      effective_end: this.fsc_form.get('end_date')?.value,
      contact_type: this.fsc_form.get('contact_type')?.value
    }

    if(this.fsc_form.get('contact_type')?.value !== 'national')
      postData['contact_uuid'] = this.fsc_form.get('contact_uuid')?.value;
    else
      postData['effective_area'] = this.fsc_form.get('default_area')?.value;

    if(this.fsc_form.get('is_fca_offset')?.value === true){
      postData['ltl_fca_offset'] = this.fsc_form.get('ltl_fca_offset')?.value / 100.00;
      postData['tl_fca_offset'] = this.fsc_form.get('tl_fca_offset')?.value / 100.00;
      postData['intermodal_offset'] = this.fsc_form.get('intermodal_offset')?.value / 100.00;
    }
    else{
      postData['ltl_rate'] = this.fsc_form.get('ltl_fsc')?.value / 100.00;
      postData['tl_rate'] = this.fsc_form.get('tl_fsc')?.value / 100.00;
    }

    // add contact info to postdata
    // if(this.fsc_form.get('rate_type')?.value == this.RATE_TYPES[1]){
    //   postData['customer_name'] = this.fsc_form.get('contact_name')?.value;
    //   postData['customer_contact_uuid'] = this.fsc_form.get('contact_uuid')?.value;
    // }
    // else if (this.fsc_form.get('rate_type')?.value == this.RATE_TYPES[2]){
    //   postData['carrier_name'] = this.fsc_form.get('contact_name')?.value;
    //   postData['carrier_contact_uuid'] = this.fsc_form.get('contact_uuid')?.value;
    // }

    // add rates
    // if(this.fsc_form.get('is_fca_offset')?.value){
    //   if(this.fsc_form.get('rate_type')?.value == this.RATE_TYPES[1]){
    //     postData['chargeable_tl_plus_rate'] = this.fsc_form.get('tl_fca_offset')?.value / 100;
    //     postData['chargeable_ltl_plus_rate'] = this.fsc_form.get('ltl_fca_offset')?.value / 100;
    //   }
    //   else{
    //     postData['cost_tl_plus_rate'] = this.fsc_form.get('tl_fca_offset')?.value / 100;
    //     postData['cost_ltl_plus_rate'] = this.fsc_form.get('ltl_fca_offset')?.value / 100;
    //   }
    // }
    // else{
    //   switch(this.fsc_form.get('rate_type')?.value){
    //     case this.RATE_TYPES[0]:
    //       postData['default_ltl_rate'] = this.fsc_form.get('ltl_fsc')?.value / 100;
    //       postData['default_tl_rate'] = this.fsc_form.get('tl_fsc')?.value / 100;
    //       break;
    //     case this.RATE_TYPES[1]:
    //       postData['chargeable_ltl_rate'] = this.fsc_form.get('ltl_fsc')?.value / 100;
    //       postData['chargeable_tl_rate'] = this.fsc_form.get('tl_fsc')?.value / 100;
    //       break;
    //     case this.RATE_TYPES[2]:
    //       postData['cost_ltl_rate'] = this.fsc_form.get('ltl_fsc')?.value / 100;
    //       postData['cost_tl_rate'] = this.fsc_form.get('tl_fsc')?.value / 100;
    //       break;
    //   }
    // }
    console.log(JSON.stringify(postData));

    const url = "/api/rates/fsc_2.php";
    this.http.post<any>(url, postData).subscribe(
      (response) => {
        if(response.code === 200) this.toastr.success("Rate has been updated")
        else{
          this.toastr.error("Rate unsuccessfully updated")
          console.log(response)
        } 
        this.fsc_form.reset();
      },
      (error) => {
        this.toastr.error("Rate unsuccessfully updated")
        console.error(error);
      }
    )
  }
}

// LTL/TL offset/FSC are required
export function ratesRequiredValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const formGroup = control as FormGroup;
    const calculation_method = formGroup.get('calculation_method');
    const ltl_fsc = formGroup.get('ltl_fsc');
    const tl_fsc = formGroup.get('tl_fsc');
    const ltl_offset = formGroup.get('ltl_fca_offset');
    const tl_offset = formGroup.get('tl_fca_offset');

    if (calculation_method?.value === 'REFERENCE_OFFSET'){
      if(ltl_offset?.value == 0 || tl_offset?.value == 0){
        return {fca_offset_required: true}
      }
    }
    else{
      if(ltl_fsc?.value == 0 || tl_fsc?.value == 0){
        return {fsc_required: true}
      }
    }
    return null;
  }
}

// If contact type is not national, a contact is required
export function contactRequiredValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const formGroup = control as FormGroup;
    if (formGroup.get('contact_type')?.value !== 'national' && formGroup.get('contact_uuid')?.value.length == 0 )
      return {contact_required: true}
    return null;
  }
}

// the start date must be before end date
export function dateRangeValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const formGroup = control as FormGroup;
    if (formGroup.get('start_date')?.value >= formGroup.get('end_date')?.value)
      return {startDateAfterEndDate: true};
    return null;
  }
}
